import React, { useEffect, useState, useRef } from "react";
import { getStyles } from "../../../assets/js/utils/functions";
import { getSizedImage } from "../../../assets/js/utils/utils";
import useScreenSize, { BreakPoint } from "../../hooks/useScreenSize";
import useSlider from "../../hooks/useSlider";
import LazyImage from "../../lazy-image";
import { AppBtn } from "../../ui/buttons";
import AnimatedHeaderText from "../../front-pages/commons/animated-header-text";
import { useFetcher, useRequest } from "@/api/utils";
import { StoreCategory } from "@/assets/interfaces";
import CategoryLoader from "../items/category-loader";
import Router from "next/router";
import { GetStoreCategoriesWithItems } from "@/api/stores";
import StorefrontContext from "@/contexts/storefront";
import classNames from "classnames";

interface Props {
  storeId: string;
  store: string;
}

const StoreCategories: React.FC<Props> = ({ storeId, store }) => {
  const { categoriesWithItems, isLoadingCategories, paths } = StorefrontContext.useContainer();
  const categories = categoriesWithItems.sort((a, b) => b.items_count - a.items_count).slice(0, 3);

  if (isLoadingCategories) {
    return (
      <div className={classNames("w-full mx-auto mt-7.5")}>
        <div className="store-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <div className="w-full bg-grey-loader rounded-15 aspect-[1.75/1] md:aspect-square animate-pulse"></div>
            <div className="flex h-full flex-row md:flex-col items-stretch gap-4">
              <div className="w-auto md:w-full aspect-square md:aspect-auto bg-grey-loader rounded-15 flex-1 animate-pulse"></div>
              <div className="w-auto md:w-full aspect-square md:aspect-auto bg-grey-loader rounded-15 flex-1 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (categories.length < 3) return null;

  return (
    <div className={classNames("w-full mx-auto mt-10")}>
      <div className="store-container">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          <div className="w-full rounded-15 aspect-[1.75/1] md:aspect-square">
            <CategoryItem category={categories[0]} paths={paths} />
          </div>
          <div className="flex h-full flex-row md:flex-col items-stretch gap-4">
            <div className="w-auto md:w-full aspect-square md:aspect-auto rounded-15 flex-1">
              <CategoryItem category={categories[1]} paths={paths} />
            </div>
            <div className="w-auto md:w-full aspect-square md:aspect-auto rounded-15 flex-1">
              <CategoryItem category={categories[2]} paths={paths} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CategoryItem: React.FC<{ category: StoreCategory; paths: any }> = ({ category, paths }) => {
  return (
    <article
      onClick={() => Router.push(`${paths.products}?category=${category.id}`)}
      className="flex-shrink-0 w-full h-full relative item-card overflow-hidden rounded-15 sell-cat-item group"
    >
      <figure className="absolute top-0 left-0 h-full w-full rounded-15 safari-overflow-fix transition-transform duration-300 ease-out">
        <LazyImage
          src={category?.first_item ? category?.first_item?.images[0] : ""}
          alt={category.name}
          className="h-full w-full object-cover rounded-15 group-hover:scale-105 will-change-transform backface-visibility-hidden"
        />
      </figure>
      <button
        type="button"
        className="absolute rounded-full top-4 right-4 h-10 w-10 bg-white text-primary-500 transition-all duration-300 ease-out group-hover:opacity-100 transform group-hover:rotate-[-40deg] flex items-center justify-center"
      >
        {/* prettier-ignore */}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="hidden group-hover:inline-block">
          <path d="M6 0L4.9425 1.0575L9.1275 5.25H0V6.75H9.1275L4.9425 10.9425L6 12L12 6L6 0Z" fill="currentColor" />
        </svg>
        {/* prettier-ignore */}
        <svg width="22" viewBox="0 0 24 24" fill="none" className="group-hover:hidden">
          <path d="M8.38 12L10.79 14.42L15.62 9.57996" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10.75 2.44995C11.44 1.85995 12.57 1.85995 13.27 2.44995L14.85 3.80995C15.15 4.06995 15.71 4.27995 16.11 4.27995H17.81C18.87 4.27995 19.74 5.14995 19.74 6.20995V7.90995C19.74 8.29995 19.95 8.86995 20.21 9.16995L21.57 10.7499C22.16 11.4399 22.16 12.5699 21.57 13.2699L20.21 14.8499C19.95 15.1499 19.74 15.7099 19.74 16.1099V17.8099C19.74 18.8699 18.87 19.7399 17.81 19.7399H16.11C15.72 19.7399 15.15 19.9499 14.85 20.2099L13.27 21.5699C12.58 22.1599 11.45 22.1599 10.75 21.5699L9.17 20.2099C8.87 19.9499 8.31 19.7399 7.91 19.7399H6.18C5.12 19.7399 4.25 18.8699 4.25 17.8099V16.0999C4.25 15.7099 4.04 15.1499 3.79 14.8499L2.44 13.2599C1.86 12.5699 1.86 11.4499 2.44 10.7599L3.79 9.16995C4.04 8.86995 4.25 8.30995 4.25 7.91995V6.19995C4.25 5.13995 5.12 4.26995 6.18 4.26995H7.91C8.3 4.26995 8.87 4.05995 9.17 3.79995L10.75 2.44995Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div className="w-full h-[100%] absolute top-0 store-cat-overlay left-0 flex flex-col justify-center items-center px-4.5 md:px-5 text-left text-md sm:text-1sm lg:text-lg">
        <div className="absolute bottom-3.5 left-3.5 right-3.5 sm:bottom-5 sm:left-5 sm:right-5 overflow-hidden">
          <h2 className="text-white font-bold !leading-tight text-[17px] sm:text-xl max-w-full overflow-hidden truncate">
            {category.name}
          </h2>
          <p className="text-white !leading-tight text-1xs md:text-sm mt-px text-opacity-80">
            {category.items_count} Items Available
          </p>
        </div>
      </div>
    </article>
  );
};

export default StoreCategories;
